import React, { useState, useEffect, useRef, useCallback } from 'react';

const DAYS   = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
];

/**
 * CalendarPicker
 * Props:
 *   value      – "YYYY-MM-DD" string or ""
 *   onChange   – (dateStr: "YYYY-MM-DD") => void
 *   minDate    – "YYYY-MM-DD" optional minimum selectable date
 *   placeholder – string shown when no date selected
 *   id         – id for the trigger button
 */
export default function CalendarPicker({ value, onChange, minDate, placeholder = 'Select date', id }) {
    const today   = new Date();
    const initDate = value ? new Date(value + 'T00:00:00') : today;

    const [open,     setOpen]     = useState(false);
    const [viewYear, setViewYear] = useState(initDate.getFullYear());
    const [viewMonth,setViewMonth]= useState(initDate.getMonth());
    const [selDate,  setSelDate]  = useState(value || '');

    const wrapRef = useRef(null);

    /* Sync if parent resets value */
    useEffect(() => {
        setSelDate(value || '');
        if (value) {
            const d = new Date(value + 'T00:00:00');
            setViewYear(d.getFullYear());
            setViewMonth(d.getMonth());
        }
    }, [value]);

    /* Close on outside click */
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const toStr = useCallback((y, m, d) => `${y}-${String(m + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`, []);

    const minParts = minDate ? minDate.split('-').map(Number) : null;
    const isDisabled = (y, m, d) => {
        if (!minParts) return false;
        const [my, mm, md] = minParts;
        return y < my || (y === my && m + 1 < mm) || (y === my && m + 1 === mm && d < md);
    };

    /* Build calendar grid */
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    const handleDay = (d) => {
        if (!d || isDisabled(viewYear, viewMonth, d)) return;
        const str = toStr(viewYear, viewMonth, d);
        setSelDate(str);
        onChange(str);
        setOpen(false);
    };

    const prevMonth = () => {
        if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
        else setViewMonth(m => m - 1);
    };
    const nextMonth = () => {
        if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
        else setViewMonth(m => m + 1);
    };

    const todayStr = toStr(today.getFullYear(), today.getMonth(), today.getDate());

    /* Format display */
    const displayValue = selDate
        ? new Date(selDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        : '';

    return (
        <div ref={wrapRef} style={{ position: 'relative', userSelect: 'none' }}>
            {/* Trigger button */}
            <button
                id={id}
                type="button"
                onClick={() => setOpen(o => !o)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '10px 14px',
                    background: 'var(--bg-base)',
                    border: `1px solid ${open ? 'var(--border-focus)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)',
                    color: selDate ? 'var(--text-primary)' : 'var(--text-muted)',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                    outline: 'none',
                    boxShadow: open ? '0 0 0 3px var(--accent-dim)' : 'none',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                }}
                aria-haspopup="true"
                aria-expanded={open}
            >
                <span style={{ fontSize: '1.1rem' }}>📅</span>
                <span style={{ flex: 1 }}>{displayValue || placeholder}</span>
                {selDate && (
                    <span
                        role="button"
                        tabIndex={0}
                        title="Clear date"
                        onClick={e => { e.stopPropagation(); setSelDate(''); onChange(''); }}
                        onKeyDown={e => e.key === 'Enter' && (e.stopPropagation(), setSelDate(''), onChange(''))}
                        style={{ color: 'var(--text-muted)', fontSize: '0.8rem', lineHeight: 1, padding: '2px 4px', borderRadius: 4 }}
                    >✕</span>
                )}
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{open ? '▲' : '▼'}</span>
            </button>

            {/* Calendar dropdown */}
            {open && (
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    left: 0,
                    zIndex: 999,
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-lg)',
                    boxShadow: 'var(--shadow-lg)',
                    padding: '16px',
                    minWidth: '280px',
                    animation: 'fadeSlideDown 0.15s ease',
                }}>
                    {/* Month / Year header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                        <button type="button" onClick={prevMonth} style={navBtnStyle}>‹</button>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <select
                                value={viewMonth}
                                onChange={e => setViewMonth(Number(e.target.value))}
                                style={selectStyle}
                            >
                                {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                            </select>
                            <select
                                value={viewYear}
                                onChange={e => setViewYear(Number(e.target.value))}
                                style={selectStyle}
                            >
                                {Array.from({ length: 12 }, (_, i) => today.getFullYear() - 1 + i).map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>
                        <button type="button" onClick={nextMonth} style={navBtnStyle}>›</button>
                    </div>

                    {/* Day labels */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '6px' }}>
                        {DAYS.map(d => (
                            <div key={d} style={{ textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', padding: '4px 0', letterSpacing: '0.05em' }}>
                                {d}
                            </div>
                        ))}
                    </div>

                    {/* Day cells */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                        {cells.map((d, i) => {
                            if (!d) return <div key={`e-${i}`} />;
                            const str = toStr(viewYear, viewMonth, d);
                            const isToday    = str === todayStr;
                            const isSelected = str === selDate;
                            const disabled   = isDisabled(viewYear, viewMonth, d);
                            return (
                                <button
                                    key={str}
                                    type="button"
                                    onClick={() => handleDay(d)}
                                    disabled={disabled}
                                    title={str}
                                    style={{
                                        width: '100%',
                                        aspectRatio: '1',
                                        border: 'none',
                                        borderRadius: '50%',
                                        background: isSelected
                                            ? 'var(--accent)'
                                            : isToday
                                                ? 'var(--accent-dim)'
                                                : 'transparent',
                                        color: isSelected
                                            ? '#fff'
                                            : isToday
                                                ? 'var(--accent-light)'
                                                : disabled
                                                    ? 'var(--text-muted)'
                                                    : 'var(--text-primary)',
                                        fontSize: '0.82rem',
                                        fontWeight: isSelected || isToday ? 700 : 400,
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        opacity: disabled ? 0.35 : 1,
                                        transition: 'background 0.15s',
                                        outline: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    onMouseEnter={e => { if (!isSelected && !disabled) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                                    onMouseLeave={e => { if (!isSelected && !disabled) e.currentTarget.style.background = isToday ? 'var(--accent-dim)' : 'transparent'; }}
                                >
                                    {d}
                                </button>
                            );
                        })}
                    </div>

                    {/* Footer: Today shortcut */}
                    <div style={{ borderTop: '1px solid var(--border)', marginTop: '12px', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <button
                            type="button"
                            onClick={() => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); }}
                            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.78rem', cursor: 'pointer', padding: '4px 8px', borderRadius: 'var(--radius-sm)' }}
                        >
                            ← This month
                        </button>
                        <button
                            type="button"
                            onClick={() => { const t = toStr(today.getFullYear(), today.getMonth(), today.getDate()); setSelDate(t); onChange(t); setOpen(false); }}
                            style={{ background: 'var(--accent-dim)', border: '1px solid rgba(99,102,241,0.3)', color: 'var(--accent-light)', fontSize: '0.78rem', cursor: 'pointer', padding: '4px 12px', borderRadius: 'var(--radius-sm)', fontWeight: 600 }}
                        >
                            Today
                        </button>
                    </div>
                </div>
            )}

            {/* Keyframe animation */}
            <style>{`
                @keyframes fadeSlideDown {
                    from { opacity: 0; transform: translateY(-6px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}

const navBtnStyle = {
    background: 'var(--bg-hover)',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-secondary)',
    fontSize: '1.2rem',
    width: '30px',
    height: '30px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
};

const selectStyle = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-primary)',
    fontSize: '0.82rem',
    padding: '4px 6px',
    cursor: 'pointer',
    outline: 'none',
};
